import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';

import { CarrotDown } from '../../assets/svg/carrot-down';
import { CarrotUp } from '../../assets/svg/carrot-up';
import { AlmaLeaf } from '../../assets/svg/leaf';
import { AlmaLink } from '../alma-link';
import { PortableText } from '../portableText/portable-text';
import * as styles from './footer.module.css';
import { Links } from './links';

export function Footer({ footerContent }) {
	// state
	const [aipOpen, setAipOpen] = useState(true);
	const [otherInsuranceOpen, setOtherInsuranceOpen] = useState(false);

	const socialIcons = footerContent?.socialIcons;

	// run query for states and insurance data
	const statesQuery = useStaticQuery(graphql`
		query {
			states: allSanityState(sort: { title: ASC }) {
				edges {
					node {
						id
						title
						stateSlug
						stateStatus
						displayInFooter
					}
				}
			}
			insurance: allSanityInsurer(
				sort: [
					{ insurerCategory: { orderRank: ASC } }
					{ orderRank: ASC }
				]
			) {
				edges {
					node {
						id
						title
						value
						insurerSlug
						is_blue_cross_blue_shield
						is_aip
						displayInFooter
					}
				}
			}
		}
	`);

	// map states data from nodes to usable array
	const statesData = statesQuery?.states?.edges;
	const mappedStatesData = statesData
		?.map((state) => {
			return state?.node;
		})
		.filter((state) => {
			return state?.displayInFooter;
		});

	// map insurance data from nodes to usable array
	const insuranceData = statesQuery?.insurance?.edges;
	const mappedInsuranceData = insuranceData?.map((insurer) => {
		return insurer?.node;
	});

	// filter insurance data into categories
	const aipInsuranceData = mappedInsuranceData
		?.filter((insurer) => {
			return insurer?.is_aip && insurer?.displayInFooter;
		})
		.sort((a, b) => {
			return a?.title?.localeCompare(b?.title);
		});

	const otherInsuranceData = mappedInsuranceData
		?.filter((insurer) => {
			return (
				!insurer?.is_aip &&
				!insurer?.is_blue_cross_blue_shield &&
				insurer?.displayInFooter
			);
		})
		.sort((a, b) => {
			return a?.title?.localeCompare(b?.title);
		});

	// handle show aip on enter key for accessibility
	const handleShowAipOpen = (event) => {
		if (event.keyCode === 13) {
			setAipOpen(!aipOpen);
		}
	};

	// handle show other insurance on enter key for accessibility
	const handleShowOtherInsurance = (event) => {
		if (event.keyCode === 13) {
			setOtherInsuranceOpen(!otherInsuranceOpen);
		}
	};

	return (
		<div
			className={classNames(
				'responsive-layout',
				'module-padding-v-lg',
				'module-padding-h',
				styles.moduleContainer
			)}
			id='footer'
		>
			{/* links */}
			{footerContent?.showFooterMenu && (
				<>
					<div className={styles.infoContainer}>
						{footerContent?.menuTitle && (
							<h2
								className={classNames(
									'heading-s',
									styles.infoTitle
								)}
							>
								{footerContent?.menuTitle}
							</h2>
						)}
						<div className={styles.linksContainer}>
							{footerContent?.footerItems?.map(
								(category, index) => {
									return (
										<div
											key={category?._key}
											className={
												styles.linksColumnContainer
											}
										>
											<div className={styles.linksColumn}>
												<h3 className={styles.title}>
													{category?.title}
												</h3>
												<ul className={styles.links}>
													<Links {...category} />
												</ul>
											</div>
											{index === 0 && (
												<div
													className={
														styles.socialLinks
													}
												>
													<p className={styles.title}>
														Follow Us
													</p>
													<div
														className={
															styles.socialIcons
														}
													>
														{socialIcons?.map(
															(socialIcon) => {
																return (
																	<a
																		key={
																			socialIcon?._id
																		}
																		href={
																			socialIcon?.socialUrl
																		}
																		title={
																			socialIcon?.title
																		}
																		dangerouslySetInnerHTML={{
																			__html: socialIcon?.svg,
																		}}
																	/>
																);
															}
														)}
													</div>
												</div>
											)}
										</div>
									);
								}
							)}
							<div className={styles.socialLinksMobile}>
								<p className={styles.title}>Follow Us</p>
								<div className={styles.socialIcons}>
									{socialIcons?.map((socialIcon) => {
										return (
											<a
												key={socialIcon?._id}
												href={socialIcon?.socialUrl}
												title={socialIcon?.title}
												dangerouslySetInnerHTML={{
													__html: socialIcon?.svg,
												}}
											/>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</>
			)}
			{/* states */}
			{footerContent?.showStateMenu && (
				<>
					<div className={styles.line} />
					<div className={styles.infoContainer}>
						{footerContent?.stateMenuTitle && (
							<h2
								className={classNames(
									'heading-s',
									styles.infoTitle
								)}
							>
								{footerContent?.stateMenuTitle}
							</h2>
						)}
						<div className={styles.statesContainer}>
							{mappedStatesData?.map((state) => {
								return (
									<div
										key={state?.id}
										className={styles.state}
									>
										<a
											className='body'
											href={`https://secure.helloalma.com/providers-results/${state?.stateSlug}?footer=true`}
										>
											{state?.title}
										</a>
									</div>
								);
							})}
						</div>
					</div>
				</>
			)}
			{/* insurance */}
			{footerContent?.showInsuranceMenu && (
				<>
					<div className={styles.line} />
					<div className={styles.infoContainer}>
						{footerContent?.insuranceMenuTitle && (
							<h2
								className={classNames(
									'heading-s',
									styles.infoTitle
								)}
							>
								{footerContent?.insuranceMenuTitle}
							</h2>
						)}
						<div
							className={styles.toggleContainer}
							onClick={() => setAipOpen(!aipOpen)}
							tabIndex='0'
							onKeyDown={(event) => handleShowAipOpen(event)}
						>
							<h3
								id='insurancePartners'
								className={styles.toggleTitle}
							>
								Alma Insurance Partners
							</h3>
							{aipOpen ? <CarrotDown /> : <CarrotUp />}
						</div>
						{aipOpen && (
							<div className={styles.aipInsuranceContainer}>
								{aipInsuranceData?.map((insurer) => {
									return (
										<div
											key={insurer?.id}
											className={styles.state}
										>
											<a
												className='body'
												href={`https://secure.helloalma.com/providers-results/${insurer?.insurerSlug}?footer=true`}
											>
												{insurer?.title}
											</a>
										</div>
									);
								})}
							</div>
						)}
						<div
							className={styles.toggleContainer}
							onClick={() =>
								setOtherInsuranceOpen(!otherInsuranceOpen)
							}
							onKeyDown={(event) =>
								handleShowOtherInsurance(event)
							}
							tabIndex='0'
						>
							<h3 className={styles.toggleTitle}>
								Other Insurance
							</h3>
							{otherInsuranceOpen ? <CarrotDown /> : <CarrotUp />}
						</div>
						{otherInsuranceOpen && (
							<div className={styles.otherInsuranceContainer}>
								{otherInsuranceData?.map((insurer) => {
									return (
										<div
											key={insurer?.id}
											className={styles.state}
										>
											<a
												className='body'
												href={`https://secure.helloalma.com/providers-results/${insurer?.insurerSlug}?footer=true`}
											>
												{insurer?.title}
											</a>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</>
			)}
			<div className={styles.leafLine}>
				<div className={styles.almaLeaf}>
					<AlmaLeaf />
				</div>
				<div className={styles.line} />
			</div>
			{/* disclaimer / privacy policy */}
			<div className={styles.footerText}>
				{footerContent?.helpDisclaimer && (
					<div className={styles.disclaimer}>
						<PortableText blocks={footerContent?.helpDisclaimer} />
					</div>
				)}
				<div className={styles.policyLinksContainer}>
					{footerContent?.policyLinks?.map((link) => {
						return (
							<div className={styles.policyLink} key={link?._key}>
								<AlmaLink cta={link} />
								<p>•</p>
							</div>
						);
					})}
					<p>Copyright Alma, {new Date().getFullYear()}</p>
				</div>
			</div>
		</div>
	);
}
